import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import axiosInstance from '../../axiosConfig';
import {
    IFacilityShareContentType,
    IFacilitySharePayload,
    IFacilityShareResponse,
} from '../../dto/FacilitySharing.dto';
import { AxiosErrorToast } from '../general/ReusableFunctions';

const buildFacilitySharePath = (
  companyId: number,
  contentType: IFacilityShareContentType,
  contentId: number,
): string => {
  if (contentType === 'event-message') {
    return `/v1/company/${companyId}/message/event-message/${contentId}/facility-share`;
  }

  return `/v1/company/${companyId}/${contentType}/${contentId}/facility-share`;
};

export const getFacilityShareApiPath = buildFacilitySharePath;

export const getFacilityShareQueryKey = (
  companyId: number,
  contentType: IFacilityShareContentType,
  contentId: number,
) => [buildFacilitySharePath(companyId, contentType, contentId)] as const;

const saveFacilityShare = (
  companyId: number,
  contentType: IFacilityShareContentType,
  contentId: number,
  payload: IFacilitySharePayload,
) => {
  return axiosInstance
    .post(
      buildFacilitySharePath(companyId, contentType, contentId),
      payload,
    )
    .then((response: AxiosResponse<IFacilityShareResponse>) => response.data)
    .catch((error: AxiosError) => {
      AxiosErrorToast(error, 'Error saving facility share');
      throw error;
    });
};

const disableFacilityShare = (
  companyId: number,
  contentType: IFacilityShareContentType,
  contentId: number,
) => {
  return axiosInstance
    .delete(buildFacilitySharePath(companyId, contentType, contentId))
    .then(() => {
      return;
    })
    .catch((error: AxiosError) => {
      AxiosErrorToast(error, 'Error disabling facility share');
      throw error;
    });
};

export const useRqFacilityShare = (
  companyId: number,
  contentType: IFacilityShareContentType,
  contentId: number,
  invalidateQueryKeys: QueryKey[] = [],
) => {
  const queryClient = useQueryClient();
  const primaryKey = getFacilityShareQueryKey(companyId, contentType, contentId);

  const invalidateShareQueries = async () => {
    await queryClient.invalidateQueries({queryKey: primaryKey});

    await Promise.all(
      invalidateQueryKeys.map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      ),
    );
  };

  const saveMutation = useMutation({
    mutationFn: (payload: IFacilitySharePayload) =>
      saveFacilityShare(companyId, contentType, contentId, payload),
    onSuccess: async () => {
      await invalidateShareQueries();
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => disableFacilityShare(companyId, contentType, contentId),
    onSuccess: async () => {
      await invalidateShareQueries();
    },
  });

  return {
    saveMutation,
    disableMutation,
  };
};

export const saveFacilityShareRequest = saveFacilityShare;

export const disableFacilityShareRequest = disableFacilityShare;